import React, { useRef, useEffect, useState, useContext, useCallback, forwardRef, useImperativeHandle } from 'react';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import gjsMjml from 'grapesjs-mjml';
import {
  Save, Download, Eye, Monitor, Tablet, Smartphone, Undo2, Redo2,
  Code, PanelLeft, ChevronLeft, X, Upload, FolderOpen,
  Trash2, Copy, FileDown, FileUp, Check, AlertCircle, Info
} from 'lucide-react';
import AuthContext from 'context/AuthContext';
import useCan from 'hooks/useCan';
import { getVariables, getTemplate } from 'Services/storeService';
import { CAPABILITIES } from 'utils/capabilities';
import TemplateManager from '../../../../utils/templateManager';
import CustomImageUploader from './components/CustomImageUploader';
import './GrapesJsEditor.css';
import logger from 'utils/logger';

/**
 * GrapeJS MJML Email Editor
 * Professional email editor with MJML for email-compatible HTML
 * @param {string} nodeId - The ID of the email node being edited (optional, for multi-node support)
 */
const GrapesJsEditor = forwardRef(({ nodeId = 'default', templateId, initialHtml, initialMjml, flowId }, ref) => {
  const editorRef = useRef(null);
  const grapesjsEditor = useRef(null);
  const [variables, setVariables] = useState([]);
  const [showVariableDropdown, setShowVariableDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [filteredVariables, setFilteredVariables] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeDevice, setActiveDevice] = useState('Desktop');
  const [activeSideTab, setActiveSideTab] = useState('blocks');
  const [showSidePanel, setShowSidePanel] = useState(true);
  const [editorReady, setEditorReady] = useState(false);

  const showDropdownRef = useRef(false);
  const filteredVarsRef = useRef([]);
  const selectedIndexRef = useRef(0);
  // Always point at the latest variables array so the editor init closure can read them
  const variablesRef = useRef([]);
  // Stores the original (sanitized, pre-chip) template content so we can re-apply chips
  // if variables arrive after the editor has already loaded the template.
  const rawTemplateContentRef = useRef(null);

  useEffect(() => { showDropdownRef.current = showVariableDropdown; }, [showVariableDropdown]);
  useEffect(() => { filteredVarsRef.current = filteredVariables; }, [filteredVariables]);
  useEffect(() => { selectedIndexRef.current = selectedIndex; }, [selectedIndex]);
  useEffect(() => { variablesRef.current = variables; }, [variables]);

  // Template Management State
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [editableCode, setEditableCode] = useState('');
  const [notification, setNotification] = useState(null);
  const fileInputRef = useRef(null);

  // Asset Manager State
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [imageUploaderProps, setImageUploaderProps] = useState(null);

  // Refs for asset manager callbacks to avoid stale closure capture inside grapesjs.init
  const setShowImageUploaderRef = useRef(setShowImageUploader);
  const setImageUploaderPropsRef = useRef(setImageUploaderProps);
  useEffect(() => { setShowImageUploaderRef.current = setShowImageUploader; }, [setShowImageUploader]);
  useEffect(() => { setImageUploaderPropsRef.current = setImageUploaderProps; }, [setImageUploaderProps]);


  const { userProfile } = useContext(AuthContext);
  const storeId = userProfile?.store?.id;
  const { allowed: canManageCommunications } = useCan(CAPABILITIES.COMMUNICATIONS_MANAGE);

  // Fetch variables
  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const response = await getVariables(flowId);
        if (response?.data && response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          const apiVariables = response.data.data.map(v => ({
            label: v.description, // Favor descriptions for all UI labels
            value: v.code || v.key,
            name: v.name || '',
            description: v.description || v.example_value || '',
            type: v.type || undefined,
          }));
          setVariables(apiVariables);
        } else {
          setVariables([]);
        }
      } catch (error) {
        logger.error('Error fetching variables:', error);
        setVariables([]);
      }
    };
    fetchVariables();
  }, [storeId, flowId]);

  // ── Variable Handling Helpers ──

  // Sanitize old placeholder URLs from saved databases
  const sanitizePlaceholders = useCallback((content) => {
    if (!content || typeof content !== 'string') return content;
    return content
      .replace(/https:\/\/via\.placeholder\.com\/180x60\?text=Murphy\+Door/gi, 'https://dummyimage.com/180x60/f0f0f0/666666&text=Murphy+Door')
      .replace(/https:\/\/via\.placeholder\.com\/120x120\?text=Murphy/gi, 'https://dummyimage.com/120x120/f0f0f0/666666&text=Murphy')
      .replace(/https:\/\/via\.placeholder\.com\/120x40\?text=Purebrand/gi, 'https://dummyimage.com/120x40/f0f0f0/666666&text=Purebrand')
      .replace(/https:\/\/via\.placeholder\.com\/150x50\?text=Purebrand/gi, 'https://dummyimage.com/150x50/f0f0f0/666666&text=Purebrand')
      .replace(/https:\/\/via\.placeholder\.com\/80x80\?text=MD/gi, 'https://dummyimage.com/80x80/1a262a/ffffff&text=MD')
      .replace(/https:\/\/via\.placeholder\.com\/[^\s"']+/gi, 'https://dummyimage.com/600x400/f0f0f0/666666&text=Placeholder');
  }, []);

  // Convert {{code}} tokens in MJML text to styled chip spans
  const tokenToMjmlHtml = useCallback((content) => {
    if (!content || typeof content !== 'string') return content;
    // Regex matches either a full tag <...> or a token {{...}}
    // Group 1: Tag (ignore these)
    // Group 2: Token match (full {{code}})
    // Group 3: Token code (the 'code' part)
    return content.replace(/(<[^>]+>)|(\{\{([^}]+)\}\})/g, (match, tag, tokenMatch, code) => {
      if (tag) return tag; // Return tags as-is (effectively skips variables inside attributes)

      const cleanCode = code.trim().toLowerCase();
      const v = variablesRef.current.find(
  (x) => x.value?.toLowerCase().trim() === cleanCode
);
      
      // Fallback logic: Description || Name || Label || Code
   const label = v?.description || '';
      
      // Log for debug if the user is still seeing codes
      if (!v) {
        logger.warn(`Variable not found for code: "${code}" (Clean: "${cleanCode}")`);
      }
      
      if (v?.type === 'image' || /logo|profile_image/i.test(code)) {
        const normalized = code.trim();
        return `<img src="{{${normalized}}}" alt="${label}" data-variable="{{${normalized}}}" contenteditable="false" style="max-height:80px;display:inline-block;vertical-align:middle;border:2px dashed #3b82f6;padding:2px;border-radius:4px;" />`;
      }
      const normalized = code.trim();
      return `<span data-variable="{{${normalized}}}" contenteditable="false" style="background-color:#dbeafe;padding:3px 8px;border-radius:4px;color:#1e40af;font-weight:inherit;border:1px dashed #3b82f6;display:inline-block;margin:0 2px;max-width:100%;white-space:normal;word-break:break-word;">${label}</span>`;
    });
  }, [variables]);

  // Strip chip elements back to {{code}} tokens before saving.
  //
  // DOM-based (not regex) so that ANY formatting applied to a chip survives the
  // round-trip: e.g. bolding a variable wraps the chip as
  // `<strong><span data-variable="{{x}}">…</span></strong>`. We replace only the
  // chip element with its token, leaving the surrounding <strong> intact, so the
  // rendered email value comes out bold. A regex with `[^<]*` would either fail
  // to match (nested markup) or drop the wrapping format.
  const chipToToken = useCallback((content) => {
    if (!content || typeof content !== 'string') return content;
    try {
      const container = document.createElement('div');
      container.innerHTML = content;
      container.querySelectorAll('[data-variable]').forEach((el) => {
        const token = el.getAttribute('data-variable') || '';
        el.replaceWith(document.createTextNode(token));
      });
      return container.innerHTML;
    } catch {
      // Defensive fallback to the previous behaviour if DOM parsing ever fails.
      return content
        .replace(/<span[^>]*data-variable="(\{\{[^}]+\}\})[^>]*>[\s\S]*?<\/span>/g, '$1')
        .replace(/<img[^>]*data-variable="(\{\{[^}]+\}\})[^>]*\/?>/g, '$1');
    }
  }, []);

  // Initialize GrapeJS with MJML
  useEffect(() => {
    if (editorRef.current && !grapesjsEditor.current) {
      grapesjsEditor.current = grapesjs.init({
        container: editorRef.current,
        height: '100%',
        width: 'auto',
        fromElement: false,
        storageManager: false,
        plugins: [gjsMjml],
        pluginsOpts: {
          [gjsMjml]: {
            // MJML plugin options - keep default blocks and traits
            resetBlocks: false,
            resetStyleManager: false,
            resetDevices: false,
            columnsPadding: '10px 5px',
            // Enable column resizing
            resizableColumns: true,
          }
        },
        deviceManager: {
          devices: [
            { name: 'Desktop', width: '' },
            { name: 'Tablet', width: '768px', widthMedia: '992px' },
            { name: 'Mobile', width: '375px', widthMedia: '480px' },
          ],
        },
        assetManager: {
          custom: {
            open(props) {
              setImageUploaderPropsRef.current(props);
              setShowImageUploaderRef.current(true);
            },
            close() {
              setShowImageUploaderRef.current(false);
              setImageUploaderPropsRef.current(null);
            }
          }
        },
        // Append managers to our custom panels
        panels: { defaults: [] },
        blockManager: {
          appendTo: '#blocks-panel',
        },
        styleManager: {
          appendTo: '#styles-panel',
          sectors: [
            {
              name: 'Dimension',
              open: true,
              // Note: padding and margin are intentionally excluded here.
              // MJML elements use attributes (e.g. padding="20px") not CSS styles.
              // Set padding via the Traits/Settings panel instead — it persists correctly.
              buildProps: ['width', 'height', 'max-width', 'min-height'],
            },
            {
              name: 'Typography',
              open: true,
              buildProps: ['font-family', 'font-size', 'font-weight', 'letter-spacing', 'color', 'line-height', 'text-align'],
            },
            {
              name: 'Decorations',
              open: false,
              buildProps: ['background-color', 'border-radius', 'border', 'box-shadow'],
            },
          ],
        },
        traitManager: {
          appendTo: '#traits-panel',
        },
        layerManager: {
          appendTo: '#layers-panel',
        },
      });

      const editor = grapesjsEditor.current;

      // ── Suppress non-fatal GrapesJS and Stripe errors ──
      // These are known GrapesJS internal race conditions that are harmless:
      // - parseHtml: ComponentTextView.syncContent race during unmount
      // - toggleEvents: ComponentTextView.toggleEvents race when model already destroyed
      // Stripe.js errors are from third-party scripts loaded by the app.
      const errorHandler = (event) => {
        const msg = event?.message || event?.reason?.message || '';
        if (
          msg.includes('parseHtml') ||
          msg.includes('Stripe') ||
          msg.includes('ResizeObserver loop') ||
          (msg.includes("Cannot read properties of undefined") && msg.includes("'events'"))
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return true;
        }
      };
      window.addEventListener('error', errorHandler);
      window.addEventListener('unhandledrejection', errorHandler);

      // Clean up on editor destroy
      editor.on('destroy', () => {
        window.removeEventListener('error', errorHandler);
        window.removeEventListener('unhandledrejection', errorHandler);
      });

      // ── Rich Text Editor – Enhanced Toolbar ──
      const rte = editor.RichTextEditor;
      rte.getAll().forEach(action => rte.remove(action.name || action.id));

      // Helper: separator between groups
      const addSep = (name) => rte.add(name, {
        icon: '<span style="display:inline-block;width:1px;height:22px;background:rgba(255,255,255,0.2);margin:0 4px;vertical-align:middle;"></span>',
        attributes: { title: '' },
        result: () => { },
      });

      // ── Variable-chip aware formatting ──────────────────────────────────────
      // A variable chip is a `contenteditable="false"` atom. Browsers cannot
      // reliably toggle execCommand formatting (esp. *removing* it) around such
      // atoms, which left chips stuck bold. So when the selection is a single
      // chip we deterministically wrap/unwrap it in a real formatting tag
      // ourselves; the chip inherits the style (preview) and the wrapper stays
      // around the {{token}} on save so the rendered email value is formatted.
      const CHIP_WRAP_TAGS = { bold: 'strong', italic: 'em', underline: 'u', strikeThrough: 's' };
      // Tags considered equivalent when *removing* a format, so legacy wrappers
      // (e.g. <b> from the old execCommand path) are cleaned up too.
      const CHIP_EQUIV_TAGS = {
        bold: ['b', 'strong'],
        italic: ['i', 'em'],
        underline: ['u', 'ins'],
        strikeThrough: ['s', 'strike', 'del'],
      };

      // Inline CSS prop (camelCase) + the value that turns each format OFF.
      const CHIP_FMT_PROP = {
        bold: 'fontWeight', italic: 'fontStyle',
        underline: 'textDecoration', strikeThrough: 'textDecoration',
      };
      const CHIP_FMT_OFF = {
        bold: 'normal', italic: 'normal', underline: 'none', strikeThrough: 'none',
      };
      // Temp marker so we can re-find the chip after execCommand('insertHTML')
      // replaces it with freshly-parsed DOM (the old reference goes stale).
      const FMT_MARK = 'data-fmt-sel';

      const getRteDoc = () => {
        try {
          const iframe = editor.Canvas.getFrameEl();
          return iframe.contentDocument || iframe.contentWindow.document;
        } catch { return null; }
      };

      // Returns the single chip the selection targets, or null.
      const getSelectedChip = (doc) => {
        const sel = doc && doc.getSelection && doc.getSelection();
        if (!sel || !sel.rangeCount) return null;
        const range = sel.getRangeAt(0);

        // 1) Caret / selection that lives inside one chip's own subtree.
        const chipOf = (node) => {
          let n = node;
          while (n && n !== doc.body) {
            if (n.nodeType === 1 && n.getAttribute && n.getAttribute('data-variable') != null) return n;
            n = n.parentNode;
          }
          return null;
        };
        const a = chipOf(range.startContainer);
        const b = chipOf(range.endContainer);
        if (a && a === b) return a;

        // 2) Selection that spans exactly one chip.
        const chips = Array.from(doc.querySelectorAll('[data-variable]')).filter((c) => {
          try { return range.intersectsNode(c); } catch { return false; }
        });
        if (chips.length !== 1) return null;
        const chip = chips[0];
        const selText = (sel.toString() || '').replace(/\u200b/g, '').trim();
        const chipText = (chip.textContent || '').trim();
        // Treat as chip formatting only when the selection is essentially the chip.
        if (sel.isCollapsed || !selText || selText === chipText) return chip;
        return null;
      };

      // Is the format currently active on the chip? Checks a structural wrapper
      // first (covers underline, whose decoration doesn't inherit into an
      // inline-block chip) then the computed style (covers inherited/inline).
      const isChipFormatActive = (doc, chip, kind) => {
        const parent = chip.parentElement;
        if (parent && parent.childNodes.length === 1
          && CHIP_EQUIV_TAGS[kind].includes(parent.tagName.toLowerCase())) {
          return true;
        }
        const win = doc.defaultView || window;
        const cs = win.getComputedStyle(chip);
        if (kind === 'bold') return (parseInt(cs.fontWeight, 10) >= 600) || cs.fontWeight === 'bold';
        if (kind === 'italic') return cs.fontStyle === 'italic';
        const deco = `${cs.textDecorationLine || ''} ${cs.textDecoration || ''}`;
        if (kind === 'underline') return deco.includes('underline');
        if (kind === 'strikeThrough') return deco.includes('line-through');
        return false;
      };

      // Toggle formatting on a single selected chip. Returns true if handled
      // (the caller then skips execCommand). All DOM writes go through
      // execCommand('insertHTML') so GrapesJS tracks/persists them — direct DOM
      // mutation is silently reverted by the editor, which broke un-bolding.
      const toggleChipFormat = (kind) => {
        const doc = getRteDoc();
        if (!doc) return false;
        const chip = getSelectedChip(doc);
        if (!chip) return false;

        const tag = CHIP_WRAP_TAGS[kind];
        const prop = CHIP_FMT_PROP[kind];
        const active = isChipFormatActive(doc, chip, kind);
        const sel = doc.getSelection();
        const range = doc.createRange();

        // Build the bare chip HTML (marked so we can re-select it afterwards),
        // clearing any inline format style left on the chip by older code.
        const chipClone = chip.cloneNode(true);
        chipClone.setAttribute(FMT_MARK, '1');

        let target = chip;        // what the selection will replace
        let replacement;

        if (active) {
          const parent = chip.parentElement;
          const cleanWrapper = parent && parent.childNodes.length === 1
            && CHIP_EQUIV_TAGS[kind].includes(parent.tagName.toLowerCase());
          if (cleanWrapper) {
            // Replace the whole <strong>/<em>/… wrapper with the bare chip.
            target = parent;
            chipClone.style[prop] = '';
            replacement = chipClone.outerHTML;
          } else {
            // No clean wrapper (inherited / inline / mixed): hard-disable the
            // format directly on the chip so the preview + saved value match.
            chipClone.style[prop] = CHIP_FMT_OFF[kind];
            replacement = chipClone.outerHTML;
          }
        } else {
          // Apply: wrap the chip so the {{token}} stays wrapped on save.
          chipClone.style[prop] = '';
          replacement = `<${tag}>${chipClone.outerHTML}</${tag}>`;
        }

        try {
          range.selectNode(target);
          sel.removeAllRanges();
          sel.addRange(range);
          doc.execCommand('insertHTML', false, replacement);
        } catch { return false; }

        // Re-select the freshly inserted chip so repeated toggles keep working.
        try {
          const fresh = doc.querySelector(`[${FMT_MARK}]`);
          if (fresh) {
            fresh.removeAttribute(FMT_MARK);
            const r2 = doc.createRange();
            r2.selectNode(fresh);
            sel.removeAllRanges();
            sel.addRange(r2);
          }
        } catch { /* best-effort re-selection */ }

        return true;
      };

      // ── Group 1: Basic formatting ──
      rte.add('bold', {
        icon: '<span style="font-weight:700;font-size:14px;padding:0 2px;">B</span>',
        attributes: { title: 'Bold (Ctrl+B)' },
        result: r => { if (!toggleChipFormat('bold')) r.exec('bold'); },
      });
      rte.add('italic', {
        icon: '<span style="font-style:italic;font-size:14px;padding:0 2px;">I</span>',
        attributes: { title: 'Italic (Ctrl+I)' },
        result: r => { if (!toggleChipFormat('italic')) r.exec('italic'); },
      });
      rte.add('underline', {
        icon: '<span style="text-decoration:underline;font-size:14px;padding:0 2px;">U</span>',
        attributes: { title: 'Underline (Ctrl+U)' },
        result: r => { if (!toggleChipFormat('underline')) r.exec('underline'); },
      });
      rte.add('strikeThrough', {
        icon: '<span style="text-decoration:line-through;font-size:14px;padding:0 2px;">S</span>',
        attributes: { title: 'Strikethrough' },
        result: r => { if (!toggleChipFormat('strikeThrough')) r.exec('strikeThrough'); },
      });

      addSep('sep1');

      // ── Group 2: Alignment ──
      rte.add('justifyLeft', {
        icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:middle">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/>
        </svg>`,
        attributes: { title: 'Align Left' },
        result: r => r.exec('justifyLeft'),
      });
      rte.add('justifyCenter', {
        icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:middle">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/>
        </svg>`,
        attributes: { title: 'Align Center' },
        result: r => r.exec('justifyCenter'),
      });
      rte.add('justifyRight', {
        icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:middle">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/>
        </svg>`,
        attributes: { title: 'Align Right' },
        result: r => r.exec('justifyRight'),
      });
      rte.add('justifyFull', {
        icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:middle">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
        </svg>`,
        attributes: { title: 'Justify' },
        result: r => r.exec('justifyFull'),
      });

      addSep('sep2');

      // ── Group 3: Lists ──
      rte.add('insertUnorderedList', {
        icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:middle">
          <line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/>
          <circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none"/><circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none"/>
        </svg>`,
        attributes: { title: 'Bullet List' },
        result: r => r.exec('insertUnorderedList'),
      });
      rte.add('insertOrderedList', {
        icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:middle">
          <line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/>
          <text x="1" y="8" font-size="7" fill="currentColor" stroke="none">1.</text>
          <text x="1" y="14" font-size="7" fill="currentColor" stroke="none">2.</text>
          <text x="1" y="20" font-size="7" fill="currentColor" stroke="none">3.</text>
        </svg>`,
        attributes: { title: 'Numbered List' },
        result: r => r.exec('insertOrderedList'),
      });

      addSep('sep3');

      // ── Group 4: Font size select ──
      rte.add('fontSize', {
        icon: `<select class="rte-select" title="Font Size" style="background:#2d3748;color:#fff;border:1px solid rgba(255,255,255,0.2);border-radius:4px;padding:2px 4px;font-size:12px;cursor:pointer;height:26px;">
          <option value="">Size</option>
          <option value="1">10px</option>
          <option value="2">13px</option>
          <option value="3">16px</option>
          <option value="4">18px</option>
          <option value="5">24px</option>
          <option value="6">32px</option>
          <option value="7">48px</option>
        </select>`,
        event: 'change',
        result: (r, action) => {
          const val = action.btn.querySelector('select').value;
          if (val) r.exec('fontSize', val);
        },
        update: (r, action) => { action.btn.querySelector('select').value = ''; }
      });

      // Font family select
      rte.add('fontName', {
        icon: `<select class="rte-select" title="Font Family" style="background:#2d3748;color:#fff;border:1px solid rgba(255,255,255,0.2);border-radius:4px;padding:2px 4px;font-size:12px;cursor:pointer;height:26px;max-width:100px;">
          <option value="">Font</option>
          <option value="Arial, sans-serif">Arial</option>
          <option value="Georgia, serif">Georgia</option>
          <option value="'Times New Roman', serif">Times</option>
          <option value="'Trebuchet MS', sans-serif">Trebuchet</option>
          <option value="Verdana, sans-serif">Verdana</option>
          <option value="'Courier New', monospace">Courier</option>
        </select>`,
        event: 'change',
        result: (r, action) => {
          const val = action.btn.querySelector('select').value;
          if (val) r.exec('fontName', val);
        },
        update: (r, action) => { action.btn.querySelector('select').value = ''; }
      });

      addSep('sep4');

      // ── Group 5: Colors ──
      rte.add('foreColor', {
        icon: `<span title="Text Color" style="display:flex;align-items:center;gap:3px;font-size:13px;font-weight:600;">
          <span style="border-bottom:3px solid #e53e3e;padding-bottom:1px;">A</span>
          <input type="color" value="#000000" style="width:16px;height:16px;border:none;cursor:pointer;border-radius:2px;padding:0;" />
        </span>`,
        event: 'change',
        result: (r, action) => r.exec('foreColor', action.btn.querySelector('input[type=color]').value),
      });
      rte.add('hiliteColor', {
        icon: `<span title="Highlight Color" style="display:flex;align-items:center;gap:3px;font-size:13px;">
          <span style="background:#fef08a;padding:0 3px;border-radius:2px;font-weight:600;color:#1a202c;">H</span>
          <input type="color" value="#fef08a" style="width:16px;height:16px;border:none;cursor:pointer;border-radius:2px;padding:0;" />
        </span>`,
        event: 'change',
        result: (r, action) => r.exec('hiliteColor', action.btn.querySelector('input[type=color]').value),
      });

      addSep('sep5');

      // ── Group 6: Link & Clear ──
      rte.add('link', {
        icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:middle">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
        </svg>`,
        attributes: { title: 'Insert Link' },
        result: r => {
          const url = window.prompt('Enter URL:', 'https://');
          if (url) r.exec('createLink', url);
        },
      });
      rte.add('removeFormat', {
        icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:middle">
          <path d="M4 7V4h16v3"/><path d="M9 20h6"/><path d="M13 4l-4 16"/><line x1="2" y1="2" x2="22" y2="22"/>
        </svg>`,
        attributes: { title: 'Clear Formatting' },
        result: r => r.exec('removeFormat'),
      });

      // Template loading priority: always fetch from backend API when templateId exists.
      // Wait for the editor 'load' event so MJML plugin has fully bootstrapped before
      // calling setComponents — otherwise the call silently fails on a blank canvas.
      const loadTemplateWhenReady = (editorInstance) => {

        if (templateId && templateId !== 'new-template') {
          getTemplate(templateId, 'email').then(res => {

            if (res?.data?.data) {
              const responseData = res.data.data;
              const draft = responseData.draft_version;
              const active = responseData.template?.active_version;
              const tmpl = draft || active || {};

              const mjml = tmpl.mjml_content || '';
              const html = tmpl.html_content || '';

              if (mjml.trim() && mjml.trim() !== '<mjml><mj-body></mj-body></mjml>') {
                const sanitized = sanitizePlaceholders(mjml);
                rawTemplateContentRef.current = sanitized;
                editorInstance.setComponents(tokenToMjmlHtml(sanitized));
              } else if (html.trim()) {
                const sanitized = sanitizePlaceholders(html);
                rawTemplateContentRef.current = sanitized;
                editorInstance.setComponents(tokenToMjmlHtml(sanitized));
              } else {
                editorInstance.setComponents('<mjml><mj-body></mj-body></mjml>');
              }
            } else {
              editorInstance.setComponents('<mjml><mj-body></mj-body></mjml>');
            }
          }).catch(err => {
            logger.error('Failed to load template from API', err);
            editorInstance.setComponents('<mjml><mj-body></mj-body></mjml>');
          });
        } else {
          editorInstance.setComponents('<mjml><mj-body></mj-body></mjml>');
        }
      };


      // GrapesJS fires 'load' once the canvas iframe + MJML plugin are ready.
      if (editor.getModel().get('loaded')) {

        setTimeout(() => loadTemplateWhenReady(editor), 100);
      } else {

        editor.once('load', () => {
          loadTemplateWhenReady(editor);
        });
      }


      // ── Inject MJML-native padding/margin traits on component select ──
      // MJML serializes attributes (e.g. padding="20px"), NOT CSS styles.
      // The Style Manager's 'padding' property only writes CSS and is lost on save.
      // By adding traits here, values are stored as MJML attributes and persist.
      editor.on('component:selected', (component) => {
        if (!component) return;

        // Auto-switch to settings tab when an element is clicked
        setActiveSideTab('settings');
        if (!showSidePanel) setShowSidePanel(true);
        const tagName = component.get('tagName') || '';

        // Force MJML Image Resizing directly on selection
        if (tagName.toLowerCase() === 'mj-image') {
          component.set('resizable', {
            ratioDefault: 1,
            tc: 0, bc: 0, cl: 0, cr: 0, // Only show corner handles to keep aspect ratio
            updateTarget: (el, rect, opt) => {
              const newWidth = Math.round(rect.w);
              // Update MJML attribute (saves correctly)
              component.addAttributes({ width: newWidth + 'px' });
              // Sync UI trait
              const widthTrait = component.getTrait('width');
              if (widthTrait) {
                widthTrait.set('value', newWidth + 'px');
              }
            }
          });
        }

        // Only apply padding traits to MJML elements
        if (!tagName.toLowerCase().startsWith('mj-')) return;

        const existingTraitNames = component.get('traits').map(t => t.get('name'));

        const paddingTraits = [
          { name: 'padding', label: 'Padding (All Sides)', placeholder: 'e.g. auto or 20px' },
          { name: 'padding-top', label: 'Top', placeholder: 'px' },
          { name: 'padding-right', label: 'Right', placeholder: 'px' },
          { name: 'padding-bottom', label: 'Bottom', placeholder: 'px' },
          { name: 'padding-left', label: 'Left', placeholder: 'px' },
        ];

        paddingTraits.forEach(({ name, label, placeholder }) => {
          if (!existingTraitNames.includes(name)) {
            component.addTrait({
              type: 'text',
              name,
              label,
              placeholder,
            });
          }
        });

        // ── Background color traits ──
        // MJML uses 'background-color' attribute on sections/columns
        // and 'container-background-color' on inner elements like mj-text, mj-image
        const tag = tagName.toLowerCase();
        const bgTraits = [];

        if (['mj-section', 'mj-column', 'mj-wrapper'].includes(tag)) {
          bgTraits.push({ name: 'background-color', label: 'Background Color', type: 'color' });
        }

        if (['mj-text', 'mj-image', 'mj-button', 'mj-social', 'mj-divider', 'mj-spacer', 'mj-table'].includes(tag)) {
          bgTraits.push({ name: 'container-background-color', label: 'Background Color', type: 'color' });
        }

        // mj-body also supports background-color
        if (tag === 'mj-body') {
          bgTraits.push({ name: 'background-color', label: 'Background Color', type: 'color' });
        }

        bgTraits.forEach(({ name, label, type }) => {
          if (!existingTraitNames.includes(name)) {
            component.addTrait({ type, name, label });
          }
        });

        // ── Image-specific traits ──
        if (tag === 'mj-image') {
          const imageTraits = [
            { name: 'border-radius', label: 'Border Radius', type: 'text', placeholder: 'e.g. 8px or 50%' },
            { name: 'border', label: 'Border', type: 'text', placeholder: 'e.g. 1px solid #ccc' },
            { name: 'width', label: 'Width', type: 'text', placeholder: 'e.g. 200px' },
            { name: 'height', label: 'Height', type: 'text', placeholder: 'auto' },
            { name: 'alt', label: 'Alt Text', type: 'text', placeholder: 'Image description' },
            { name: 'href', label: 'Link URL', type: 'text', placeholder: 'https://example.com' },
          ];

          imageTraits.forEach(({ name, label, type, placeholder }) => {
            if (!existingTraitNames.includes(name)) {
              const trait = { type, name, label };
              if (placeholder) trait.placeholder = placeholder;
              component.addTrait(trait);
            }
          });
        }

        // ── Button-specific traits ──
        if (tag === 'mj-button') {
          const buttonTraits = [
            { name: 'href', label: 'Link URL', type: 'text', placeholder: 'https://example.com' },
            {
              name: 'target', label: 'Open In', type: 'select', options: [
                { id: '_self', name: 'Same Tab' },
                { id: '_blank', name: 'New Tab' },
              ]
            },
            { name: 'width', label: 'Width', type: 'text', placeholder: 'e.g. 300px or 100%' },
            { name: 'inner-padding', label: 'Inner Padding', type: 'text', placeholder: 'e.g. 15px 30px' },
            { name: 'border', label: 'Border', type: 'text', placeholder: 'e.g. 2px solid #333' },
            { name: 'border-radius', label: 'Border Radius', type: 'text', placeholder: 'e.g. 8px' },
            { name: 'font-size', label: 'Font Size', type: 'text', placeholder: 'e.g. 16px' },
            { name: 'color', label: 'Text Color', type: 'color' },
            { name: 'background-color', label: 'Button Color', type: 'color' },
          ];

          buttonTraits.forEach(({ name, label, type, placeholder, options }) => {
            if (!existingTraitNames.includes(name)) {
              const trait = { type, name, label };
              if (placeholder) trait.placeholder = placeholder;
              if (options) trait.options = options;
              component.addTrait(trait);
            }
          });
        }
      });

      // ── Fix: Forward outer-document mouse events into the GrapesJS iframe during block drag ──
      // When blocks are in a custom appendTo container (outside the iframe), GrapesJS needs
      // mouse events forwarded into the iframe's document to detect drop positions.
      let iframeDragCleanup = null;

      editor.on('block:drag:start', () => {
        const iframe = editor.Canvas.getFrameEl();
        if (!iframe || !iframe.contentWindow) return;

        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

        const forwardEvent = (e) => {
          try {
            const iframeRect = iframe.getBoundingClientRect();
            const syntheticEvent = new MouseEvent(e.type, {
              bubbles: true,
              cancelable: true,
              clientX: e.clientX - iframeRect.left,
              clientY: e.clientY - iframeRect.top,
              button: e.button,
              buttons: e.buttons,
            });
            iframeDoc.dispatchEvent(syntheticEvent);
          } catch (err) {
            // Cross-origin or destroyed iframe – ignore silently
          }
        };

        document.addEventListener('mousemove', forwardEvent, true);
        document.addEventListener('mouseup', forwardEvent, true);

        iframeDragCleanup = () => {
          document.removeEventListener('mousemove', forwardEvent, true);
          document.removeEventListener('mouseup', forwardEvent, true);
        };
      });

      editor.on('block:drag:stop', () => {
        if (iframeDragCleanup) {
          iframeDragCleanup();
          iframeDragCleanup = null;
        }
      });

      setEditorReady(true);
    }

    return () => {
      if (grapesjsEditor.current) {
        const editor = grapesjsEditor.current;
        grapesjsEditor.current = null;

        // Push destroy far out to allow pending lodash debounced events to clear out naturally
        setTimeout(() => {
          try {
            editor.destroy();
          } catch (e) {
            logger.warn('GrapesJS cleanup error:', e);
          }
        }, 500);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Expose methods to the parent via forwardRef
  useImperativeHandle(ref, () => ({
    getHtml: () => {
      if (grapesjsEditor.current) {
        return chipToToken(grapesjsEditor.current.getHtml());
      }
      return '';
    },
    getCss: () => {
      if (grapesjsEditor.current) {
        return grapesjsEditor.current.getCss();
      }
      return '';
    },
    getMjml: () => {
      // If we're using MJML, expose the raw MJML with chips converted back to tokens
      if (grapesjsEditor.current) {
        return chipToToken(grapesjsEditor.current.getHtml());
      }
      return '';
    },
    getExportedHtml: async () => {
      if (!grapesjsEditor.current) return '';
      try {
        if (grapesjsEditor.current.Commands.has('mjml-code-to-html')) {
          try {
            const mjmlCode = chipToToken(grapesjsEditor.current.getHtml());
            const result = grapesjsEditor.current.runCommand('mjml-code-to-html', { code: mjmlCode });
            if (result && result.html) return result.html;
          } catch (e) {
            logger.error('Error compiling MJML:', e);
          }
        }
        // Fallback if the plugin version uses the standard getHtml
        return chipToToken(grapesjsEditor.current.getHtml());
      } catch (e) {
        logger.error('Failed to get MJML code:', e);
        return chipToToken(grapesjsEditor.current.getHtml());
      }
    }
  }));

  // ── Race-condition fallback: re-apply chip conversion if variables arrived late ──
  // If the template was fetched before getVariables() resolved, the raw content
  // was loaded without chip spans. When variables finally arrive, re-apply.
  useEffect(() => {
    if (!editorReady || !grapesjsEditor.current || variables.length === 0) return;
    const raw = rawTemplateContentRef.current;
    if (!raw || !raw.includes('{{')) return; // nothing to convert
    const converted = tokenToMjmlHtml(raw);
    
    // Only reload if the result differs or if we are forcing a refresh
    if (converted !== raw) {
      // Force a full re-render by clearing first (to avoid MJML parser caching issues)
      grapesjsEditor.current.setComponents('');
      setTimeout(() => {
        if (grapesjsEditor.current) {
          grapesjsEditor.current.setComponents(converted);
        }
      }, 50);
      // Clear to prevent future redundant reloads
      rawTemplateContentRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables, editorReady, tokenToMjmlHtml]);

  // Variable autocomplete on {{ trigger
  useEffect(() => {
    if (!grapesjsEditor.current || !editorReady || variables.length === 0) return;

    const editor = grapesjsEditor.current;

    const handleRteEnable = (rte) => {


      const iframe = editor.Canvas.getFrameEl();
      if (!iframe) {

        return;
      }

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

      const checkForTrigger = () => {
        const selection = iframeDoc.getSelection();
        if (!selection || !selection.rangeCount) return;

        try {
          const range = selection.getRangeAt(0);
          const container = range.startContainer;

          // Handle both text nodes and element nodes
          let text = '';
          let cursorPos = 0;

          if (container.nodeType === 3) { // Text node
            text = container.textContent || '';
            cursorPos = range.startOffset;
          } else if (container.nodeType === 1) { // Element node
            text = container.textContent || '';
            cursorPos = text.length;
          }

          const beforeCursor = text.substring(Math.max(0, cursorPos - 2), cursorPos);


          if (beforeCursor === '{{') {

            // Get exact cursor position
            const canvasEl = document.querySelector('.editor-canvas');
            if (!canvasEl) return;

            // Create a range at cursor position to get its coordinates
            let cursorRect;
            if (container.nodeType === 3) {
              const tempRange = iframeDoc.createRange();
              tempRange.setStart(container, cursorPos);
              tempRange.setEnd(container, cursorPos);
              cursorRect = tempRange.getBoundingClientRect();
            } else {
              cursorRect = range.getBoundingClientRect();
            }

            const canvasRect = canvasEl.getBoundingClientRect();
            const iframeRect = iframe.getBoundingClientRect();

            // Calculate position: cursor position relative to iframe + iframe position relative to canvas
            setDropdownPosition({
              top: cursorRect.bottom + iframeRect.top - canvasRect.top + 5,
              left: cursorRect.left + iframeRect.left - canvasRect.left
            });

            setFilteredVariables(variables);
            setSelectedIndex(0);
            setShowVariableDropdown(true);
          } else {
            // Check if still typing variable name
            const lastOpen = text.lastIndexOf('{{', cursorPos - 1);
            const lastClose = text.lastIndexOf('}}', cursorPos - 1);

            if (lastOpen > lastClose && lastOpen !== -1) {
              const searchText = text.substring(lastOpen + 2, cursorPos).trim().toLowerCase();
              const filtered = variables.filter(v =>
                v.label.toLowerCase().includes(searchText)
              );
              if (filtered.length > 0) {
                setFilteredVariables(filtered);
                setSelectedIndex(0);
              }
            } else if (showDropdownRef.current) {
              setShowVariableDropdown(false);
            }
          }
        } catch (err) {
          logger.error('Variable dropdown error:', err);
        }
      };

      const handleKeyDown = (e) => {
        if (!showDropdownRef.current) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, filteredVarsRef.current.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' || e.key === 'Tab') {
          if (filteredVarsRef.current.length > 0) {
            e.preventDefault();
            insertVariable(filteredVarsRef.current[selectedIndexRef.current]);
          }
        } else if (e.key === 'Escape') {
          setShowVariableDropdown(false);
        }
      };

      // Listen on both input and keyup for better detection
      iframeDoc.addEventListener('input', checkForTrigger, true);
      iframeDoc.addEventListener('keyup', checkForTrigger, true);
      iframeDoc.addEventListener('keydown', handleKeyDown, true);

      rte._varCleanup = () => {
        iframeDoc.removeEventListener('input', checkForTrigger, true);
        iframeDoc.removeEventListener('keyup', checkForTrigger, true);
        iframeDoc.removeEventListener('keydown', handleKeyDown, true);
      };
    };

    const handleRteDisable = (rte) => {

      setShowVariableDropdown(false);
      if (rte && rte._varCleanup) {
        rte._varCleanup();
      }
    };

    editor.on('rte:enable', handleRteEnable);
    editor.on('rte:disable', handleRteDisable);

    return () => {
      editor.off('rte:enable', handleRteEnable);
      editor.off('rte:disable', handleRteDisable);
    };
  }, [variables, editorReady]);

  // Insert variable into editor
  const insertVariable = (variable) => {
    const editor = grapesjsEditor.current;
    if (!editor) return;

    try {
      const iframe = editor.Canvas.getFrameEl();
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      const selection = doc.getSelection();

      if (!selection || !selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      const textNode = range.startContainer;

      if (textNode.nodeType === 3) {
        const text = textNode.textContent;
        const offset = range.startOffset;

        // Find and remove the {{ trigger
        const lastOpen = text.lastIndexOf('{{', offset);
        if (lastOpen !== -1) {
          const newRange = doc.createRange();
          newRange.setStart(textNode, lastOpen);
          newRange.setEnd(textNode, offset);
          selection.removeAllRanges();
          selection.addRange(newRange);
          doc.execCommand('delete', false, null);
        }

        // Insert variable using the centralized tokenToMjmlHtml helper
        const variableHtml = tokenToMjmlHtml(`{{${variable.value}}}`) + '&nbsp;';
        doc.execCommand('insertHTML', false, variableHtml);
      }
    } catch (error) {
      logger.error('Error inserting variable:', error);
    }

    setShowVariableDropdown(false);
  };

  // Auto-save removed as it is now handled by the parent workflow state

  // Device switching
  const switchDevice = (device) => {
    grapesjsEditor.current?.setDevice(device);
    setActiveDevice(device);
  };

  // Actions
  const handleUndo = () => grapesjsEditor.current?.UndoManager.undo();
  const handleRedo = () => grapesjsEditor.current?.UndoManager.redo();

  const handleSave = () => {
    if (grapesjsEditor.current) {
      alert('Click "Save & Close" at the top to save your changes to the workflow draft.');
    }
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear the template? This will reset to default.')) {
      if (grapesjsEditor.current) {
        // Reset to default template
        grapesjsEditor.current.setComponents(`
          <mjml>
            <mj-body background-color="#f4f4f4">
              <mj-section background-color="#ffffff" padding="20px">
                <mj-column>
                  <mj-text font-size="16px" color="#334155">Start editing your email template...</mj-text>
                </mj-column>
              </mj-section>
            </mj-body>
          </mjml>
        `);
        alert('Template cleared!');
      }
    }
  };

  const handleExport = async () => {
    if (grapesjsEditor.current) {
      try {
        let html = '';
        if (grapesjsEditor.current.Commands.has('mjml-code-to-html')) {
          try {
            const mjmlCode = grapesjsEditor.current.getHtml();
            const result = grapesjsEditor.current.runCommand('mjml-code-to-html', { code: mjmlCode });
            if (result && result.html) html = result.html;
          } catch (e) {
            logger.error('MJML compile error:', e);
          }
        }

        // Final fallback
        if (!html) {
          html = grapesjsEditor.current.getHtml();
        }
        const blob = new Blob([html], { type: 'text/html' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'email-template.html';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      } catch (error) {
        logger.error('Export error:', error);
        showNotification('Error exporting HTML: ' + error.message, 'error');
      }
    }
  };

  const handlePreview = async () => {
    if (grapesjsEditor.current) {
      try {
        let fullHtml = '';

        // Compile MJML to proper HTML with all inline styles
        if (grapesjsEditor.current.Commands.has('mjml-code-to-html')) {
          try {
            const mjmlCode = grapesjsEditor.current.getHtml();
            const result = grapesjsEditor.current.runCommand('mjml-code-to-html', { code: mjmlCode });
            if (result && result.html) {
              fullHtml = result.html;
            }
          } catch (e) {
            logger.error('MJML compile error for preview:', e);
          }
        }

        // Fallback: wrap raw MJML/HTML with basic page structure
        if (!fullHtml) {
          const html = grapesjsEditor.current.getHtml();
          const css = grapesjsEditor.current.getCss();
          fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>${css}</style>
</head>
<body>${html}</body>
</html>`;
        }

        // Open preview in new window
        const preview = window.open('', '_blank');
        if (preview) {
          preview.document.write(fullHtml);
          preview.document.close();
        } else {
          alert('Please allow pop-ups to view the preview');
        }
      } catch (error) {
        logger.error('Preview error:', error);
        alert('Error generating preview: ' + error.message);
      }
    }
  };

  const handleViewCode = () => {
    if (grapesjsEditor.current) {
      const mjml = grapesjsEditor.current.getHtml();
      setEditableCode(mjml);
      setShowCodeModal(true);
    }
  };

  const handleLoadCodeFromModal = () => {
    if (grapesjsEditor.current && editableCode.trim()) {
      try {
        grapesjsEditor.current.setComponents(editableCode);
        showNotification('Code loaded successfully!', 'success');
        setShowCodeModal(false);
      } catch (error) {
        showNotification('Error loading code: ' + error.message, 'error');
      }
    }
  };

  // ========== TEMPLATE MANAGEMENT HANDLERS ==========

  /**
   * Load saved templates list
   */
  useEffect(() => {
    const templates = TemplateManager.getAllTemplates();
    setSavedTemplates(templates);
  }, []);

  /**
   * Show notification helper
   */
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  /**
   * Save template as new with name/description
   */
  const handleSaveAs = () => {
    setShowSaveModal(true);
  };

  /**
   * Confirm save template
   */
  const handleConfirmSave = (name, description, category = 'custom') => {
    if (!grapesjsEditor.current) return;

    try {
      const mjml = grapesjsEditor.current.getHtml();
      let html = mjml;

      // Get compiled HTML
      if (grapesjsEditor.current.Commands.has('mjml-code-to-html')) {
        try {
          const result = grapesjsEditor.current.runCommand('mjml-code-to-html', { code: mjml });
          if (result && result.html) html = result.html;
        } catch (e) { logger.error(e); }
      }

      const template = {
        id: currentTemplateId || TemplateManager.generateId(),
        name: name || 'Untitled Template',
        description: description || '',
        mjml,
        html,
        category,
        tags: []
      };

      const success = TemplateManager.saveTemplate(template);

      if (success) {
        setCurrentTemplateId(template.id);
        setSavedTemplates(TemplateManager.getAllTemplates());
        setShowSaveModal(false);
        showNotification(`Template "${name}" saved successfully!`, 'success');
      } else {
        showNotification('Failed to save template', 'error');
      }
    } catch (error) {
      logger.error('Save error:', error);
      showNotification('Error saving template: ' + error.message, 'error');
    }
  };

  /**
   * Load template from saved list
   */
  const handleLoadTemplate = (templateId) => {
    if (!grapesjsEditor.current) return;

    try {
      const template = TemplateManager.loadTemplate(templateId);

      if (template) {
        const sanitized = sanitizePlaceholders(template.mjml);
        rawTemplateContentRef.current = sanitized;
        grapesjsEditor.current.setComponents(tokenToMjmlHtml(sanitized));
        setCurrentTemplateId(template.id);
        setShowLoadModal(false);
        showNotification(`Template "${template.name}" loaded!`, 'success');
      } else {
        showNotification('Template not found', 'error');
      }
    } catch (error) {
      logger.error('Load error:', error);
      showNotification('Error loading template: ' + error.message, 'error');
    }
  };

  /**
   * Delete template with confirmation
   */
  const handleDeleteTemplate = (templateId, templateName) => {
    if (!window.confirm(`Are you sure you want to delete "${templateName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const success = TemplateManager.deleteTemplate(templateId);

      if (success) {
        setSavedTemplates(TemplateManager.getAllTemplates());
        if (currentTemplateId === templateId) {
          setCurrentTemplateId(null);
        }
        showNotification(`Template "${templateName}" deleted`, 'success');
      } else {
        showNotification('Failed to delete template', 'error');
      }
    } catch (error) {
      logger.error('Delete error:', error);
      showNotification('Error deleting template: ' + error.message, 'error');
    }
  };

  /**
   * Duplicate template
   */
  const handleDuplicateTemplate = (templateId) => {
    try {
      const duplicate = TemplateManager.duplicateTemplate(templateId);

      if (duplicate) {
        setSavedTemplates(TemplateManager.getAllTemplates());
        showNotification(`Template duplicated as "${duplicate.name}"`, 'success');
      } else {
        showNotification('Failed to duplicate template', 'error');
      }
    } catch (error) {
      logger.error('Duplicate error:', error);
      showNotification('Error duplicating template: ' + error.message, 'error');
    }
  };

  /**
   * Export with options
   */
  const handleExportOptions = () => {
    setShowExportModal(true);
  };

  /**
   * Export as specific format
   */
  const handleExportFormat = async (format) => {
    if (!grapesjsEditor.current) return;

    try {
      const template = currentTemplateId
        ? TemplateManager.loadTemplate(currentTemplateId)
        : null;

      const templateName = template?.name || 'email-template';

      switch (format) {
        case 'html': {
          let html = '';
          if (grapesjsEditor.current.Commands.has('mjml-code-to-html')) {
            try {
              const mjmlCode = grapesjsEditor.current.getHtml();
              const result = grapesjsEditor.current.runCommand('mjml-code-to-html', { code: mjmlCode });
              if (result && result.html) html = result.html;
            } catch (e) { logger.error(e); }
          }

          // Final fallback: get raw MJML source
          if (!html) {
            html = grapesjsEditor.current.getHtml();
          }
          TemplateManager.downloadFile(html, `${templateName}.html`, 'text/html');
          showNotification('HTML exported successfully!', 'success');
          break;
        }

        case 'mjml':
          const mjml = grapesjsEditor.current.getHtml();
          TemplateManager.downloadFile(mjml, `${templateName}.mjml`, 'text/plain');
          showNotification('MJML exported successfully!', 'success');
          break;

        case 'json':
          if (template) {
            const json = TemplateManager.exportAsJSON(template);
            TemplateManager.downloadFile(json, `${templateName}.json`, 'application/json');
            showNotification('JSON exported successfully!', 'success');
          } else {
            showNotification('Please save template first to export as JSON', 'warning');
          }
          break;

        default:
          showNotification('Unknown export format', 'error');
      }

      setShowExportModal(false);
    } catch (error) {
      logger.error('Export error:', error);
      showNotification('Error exporting template: ' + error.message, 'error');
    }
  };

  /**
   * Import template
   */
  const handleImportClick = () => {
    setShowImportModal(true);
  };



  /**
   * Clear current template (new template)
   */
  const handleNewTemplate = () => {
    if (!grapesjsEditor.current) return;

    if (window.confirm('Start a new template? Any unsaved changes will be lost.')) {
      const newTemplateContent = `
        <mjml>
          <mj-body background-color="#f4f4f4">
            <mj-section background-color="#ffffff" padding="20px">
              <mj-column>
                <mj-text font-size="16px" color="#334155">Start editing your email template...</mj-text>
              </mj-column>
            </mj-section>
          </mj-body>
        </mjml>
      `;
      rawTemplateContentRef.current = newTemplateContent;
      grapesjsEditor.current.setComponents(newTemplateContent);
      setCurrentTemplateId(null);
      showNotification('New template created', 'info');
    }
  };

  // Import handlers
  const handleImportMJML = (mjmlCode) => {
    if (grapesjsEditor.current && mjmlCode) {
      try {
        const sanitized = sanitizePlaceholders(mjmlCode);
        rawTemplateContentRef.current = sanitized;
        grapesjsEditor.current.setComponents(tokenToMjmlHtml(sanitized));
        showNotification('MJML template imported successfully!', 'success');
        setShowImportModal(false);
      } catch (error) {
        showNotification('Error importing MJML: ' + error.message, 'error');
      }
    }
  };

  const handleFileImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      showNotification('File too large. Maximum size is 5MB.', 'error');
      return;
    }

    // Validate file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!['json', 'mjml', 'html'].includes(fileExtension)) {
      showNotification('Invalid file type. Please upload .json, .mjml, or .html files.', 'error');
      return;
    }

    try {
      const fileContent = await file.text();

      if (fileExtension === 'json') {
        // Import from JSON
        const template = JSON.parse(fileContent);
        if (template.mjml) {
          handleImportMJML(template.mjml);
          if (template.name) {
            showNotification(`Template "${template.name}" imported!`, 'success');
          }
        } else {
          showNotification('Invalid JSON template format.', 'error');
        }
      } else if (fileExtension === 'mjml' || fileExtension === 'html') {
        // Import MJML or HTML directly
        handleImportMJML(fileContent);
      }

      // Reset file input
      event.target.value = '';
    } catch (error) {
      showNotification('Error reading file: ' + error.message, 'error');
    }
  };

  return (
    <div className="mjml-editor">
      {/* Top Header */}
      <header className="editor-header">
        <div className="header-left">
          <button className="icon-btn" onClick={() => setShowSidePanel(!showSidePanel)} title="Toggle Side Panel">
            <PanelLeft size={18} />
          </button>
          <div className="header-divider" />
          <button className="icon-btn" onClick={handleUndo} title="Undo">
            <Undo2 size={18} />
          </button>
          <button className="icon-btn" onClick={handleRedo} title="Redo">
            <Redo2 size={18} />
          </button>
        </div>

        <div className="header-center">
          <div className="device-switcher">
            <button
              className={`device-btn ${activeDevice === 'Desktop' ? 'active' : ''}`}
              onClick={() => switchDevice('Desktop')}
              title="Desktop"
            >
              <Monitor size={18} />
            </button>
            <button
              className={`device-btn ${activeDevice === 'Tablet' ? 'active' : ''}`}
              onClick={() => switchDevice('Tablet')}
              title="Tablet"
            >
              <Tablet size={18} />
            </button>
            <button
              className={`device-btn ${activeDevice === 'Mobile' ? 'active' : ''}`}
              onClick={() => switchDevice('Mobile')}
              title="Mobile"
            >
              <Smartphone size={18} />
            </button>
          </div>
        </div>

        <div className="header-right">
          {/* Template Management Buttons
          <button className="icon-btn" onClick={handleNewTemplate} title="New Template">
            <PanelLeft size={18} />
          </button>
          <button className="icon-btn" onClick={() => setShowLoadModal(true)} title="Open Template">
            <FolderOpen size={18} />
          </button>
          <button className="icon-btn" onClick={handleSaveAs} title="Save As">
            <Save size={18} />
          </button> */}
          {/* <div className="header-divider" /> */}

          {/* Import/Export — import writes; export is a read. */}
          {canManageCommunications && (
          <button className="icon-btn" onClick={handleImportClick} title="Import Template">
            <Upload size={18} />
          </button>
          )}
          <button className="icon-btn" onClick={handleExportOptions} title="Export Options">
            <Download size={18} />
          </button>
          <div className="header-divider" />

          {/* Preview & Code */}
          {/* <button className="icon-btn" onClick={handleViewCode} title="View Code">
            <Code size={18} />
          </button> */}
          <button className="icon-btn" onClick={handlePreview} title="Preview">
            <Eye size={18} />
          </button>
          <div className="header-divider" />

          {/* Delete Current Template */}
          {canManageCommunications && currentTemplateId && (
            <button className="action-btn danger" onClick={() => {
              const template = TemplateManager.loadTemplate(currentTemplateId);
              if (template) handleDeleteTemplate(currentTemplateId, template.name);
            }} title="Delete Current Template">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </header>

      {/* Main Editor Area */}
      <div className="editor-body" style={!canManageCommunications ? { pointerEvents: 'none' } : undefined}>
        {/* Unified Left Panel */}
        <aside className={`left-panel ${showSidePanel ? '' : 'collapsed'}`} style={{ width: showSidePanel ? '360px' : '0' }}>
          <div className="mjml-panel-tabs" style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
            <button
              className={`mjml-tab ${activeSideTab === 'blocks' ? 'active' : ''}`}
              onClick={() => setActiveSideTab('blocks')}
              style={{ flex: 1, padding: '12px 8px', fontSize: '12px', border: 'none', background: 'transparent', cursor: 'pointer', borderBottom: activeSideTab === 'blocks' ? '2px solid #3b82f6' : '2px solid transparent', color: activeSideTab === 'blocks' ? '#3b82f6' : '#64748b', fontWeight: 500 }}
            >
              Blocks
            </button>
            <button
              className={`mjml-tab ${activeSideTab === 'styles' ? 'active' : ''}`}
              onClick={() => setActiveSideTab('styles')}
              style={{ flex: 1, padding: '12px 8px', fontSize: '12px', border: 'none', background: 'transparent', cursor: 'pointer', borderBottom: activeSideTab === 'styles' ? '2px solid #3b82f6' : '2px solid transparent', color: activeSideTab === 'styles' ? '#3b82f6' : '#64748b', fontWeight: 500 }}
            >
              Styles
            </button>
            <button
              className={`mjml-tab ${activeSideTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveSideTab('settings')}
              style={{ flex: 1, padding: '12px 8px', fontSize: '12px', border: 'none', background: 'transparent', cursor: 'pointer', borderBottom: activeSideTab === 'settings' ? '2px solid #3b82f6' : '2px solid transparent', color: activeSideTab === 'settings' ? '#3b82f6' : '#64748b', fontWeight: 500 }}
            >
              Settings
            </button>
            <button
              className={`tab ${activeSideTab === 'layers' ? 'active' : ''}`}
              onClick={() => setActiveSideTab('layers')}
              style={{ flex: 1, padding: '12px 8px', fontSize: '12px', border: 'none', background: 'transparent', cursor: 'pointer', borderBottom: activeSideTab === 'layers' ? '2px solid #3b82f6' : '2px solid transparent', color: activeSideTab === 'layers' ? '#3b82f6' : '#64748b', fontWeight: 500 }}
            >
              LAYERS
            </button>
          </div>

          <div className="panel-content" style={{ flex: 1, overflowY: 'auto' }}>
            <div id="blocks-panel" className={activeSideTab === 'blocks' ? '' : 'hidden'}></div>
            <div id="styles-panel" className={activeSideTab === 'styles' ? '' : 'hidden'}></div>
            <div id="traits-panel" className={activeSideTab === 'settings' ? '' : 'hidden'}></div>
            <div id="layers-panel" className={activeSideTab === 'layers' ? '' : 'hidden'}></div>
          </div>
        </aside>

        {/* Canvas */}
        <main className="editor-canvas">
          <div ref={editorRef} className="canvas-inner" />

          {/* Variable Dropdown */}
          {showVariableDropdown && filteredVariables.length > 0 && (
            <div
              className="variable-dropdown"
              style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              <div className="dropdown-header">Insert Variable</div>
              {filteredVariables.map((v, i) => (
                <div
                  key={v.value}
                  className={`dropdown-item ${i === selectedIndex ? 'active' : ''}`}
                  onClick={() => insertVariable(v)}
                  onMouseEnter={() => setSelectedIndex(i)}
                >
                  <span className="var-label">{v.label}</span>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* ========== MODALS ========== */}

      {/* Save Template Modal */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="modal-content-hs" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Save Template</h3>
              <button className="modal-close" onClick={() => setShowSaveModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <SaveTemplateForm
                onSave={handleConfirmSave}
                onCancel={() => setShowSaveModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Load Template Modal */}
      {showLoadModal && (
        <div className="modal-overlay" onClick={() => setShowLoadModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Load Template</h3>
              <button className="modal-close" onClick={() => setShowLoadModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <TemplateLibrary
                templates={savedTemplates}
                onLoad={handleLoadTemplate}
                onDelete={handleDeleteTemplate}
                onDuplicate={handleDuplicateTemplate}
              />
            </div>
          </div>
        </div>
      )}

      {/* Export Options Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content-hs" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Export Template</h3>
              <button className="modal-close" onClick={() => setShowExportModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="export-options">
                <button className="export-option-btn" onClick={() => handleExportFormat('html')}>
                  <FileDown size={24} />
                  <span>Export as HTML</span>
                  <small>Production-ready email HTML</small>
                </button>
                <button className="export-option-btn" onClick={() => handleExportFormat('mjml')}>
                  <FileDown size={24} />
                  <span>Export as MJML</span>
                  <small>Editable MJML source code</small>
                </button>
                <button className="export-option-btn" onClick={() => handleExportFormat('json')}>
                  <FileDown size={24} />
                  <span>Export as JSON</span>
                  <small>Complete template with metadata</small>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Template Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content-hs" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import Template</h3>
              <button className="modal-close" onClick={() => setShowImportModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="import-dropzone" onClick={() => fileInputRef.current?.click()}>
                <Upload size={48} />
                <p>Click to upload MJML or HTML template</p>
                <small>Supports .json, .mjml, and .html files (max 5MB)</small>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.mjml,.html"
                style={{ display: 'none' }}
                onChange={handleFileImport}
              />
            </div>
          </div>
        </div>
      )}
      {/* Code Editor Modal */}
      {showCodeModal && (
        <div className="modal-overlay" onClick={() => setShowCodeModal(false)}>
          <div className="modal-content-hs large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit MJML/HTML Code</h3>
              <button className="modal-close" onClick={() => setShowCodeModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="code-editor-container">
                <textarea
                  className="code-editor"
                  value={editableCode}
                  onChange={(e) => setEditableCode(e.target.value)}
                  placeholder="Paste your MJML or HTML code here..."
                  spellCheck={false}
                />
              </div>
              <div className="form-actions">
                <button className="btn-secondary" onClick={() => setShowCodeModal(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleLoadCodeFromModal}>
                  Load Code into Editor
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`notification toast-${notification.type}`}>
          {notification.type === 'success' && <Check size={20} />}
          {notification.type === 'error' && <AlertCircle size={20} />}
          {notification.type === 'info' && <Info size={20} />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Custom Asset Manager */}
      <CustomImageUploader
        isOpen={showImageUploader}
        onClose={() => {
          setShowImageUploader(false);
          if (imageUploaderProps && imageUploaderProps.close) {
            imageUploaderProps.close();
          }
        }}
        onSelectImage={(url) => {
          if (imageUploaderProps && imageUploaderProps.select) {
            imageUploaderProps.select(url);
          }
        }}
      />
    </div>
  );
});

// ========== HELPER COMPONENTS ==========

/**
 * Save Template Form Component
 */
const SaveTemplateForm = ({ onSave, onCancel }) => {
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onSave(name.trim(), description.trim());
    }
  };

  return (
    <form onSubmit={handleSubmit} className="template-form">
      <div className="form-group">
        <label htmlFor="template-name">Template Name *</label>
        <input
          id="template-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Welcome Email"
          required
          autoFocus
        />
      </div>
      <div className="form-group">
        <label htmlFor="template-desc">Description</label>
        <textarea
          id="template-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description..."
          rows={3}
        />
      </div>
      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary">
          Save Template
        </button>
      </div>
    </form>
  );
};

/**
 * Template Library Component
 */
const TemplateLibrary = ({ templates, onLoad, onDelete, onDuplicate }) => {
  const [searchQuery, setSearchQuery] = React.useState('');

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (templates.length === 0) {
    return (
      <div className="empty-state">
        <FolderOpen size={48} />
        <p>No saved templates yet</p>
        <small>Save your first template to get started</small>
      </div>
    );
  }

  return (
    <div className="template-library">
      <div className="library-search">
        <input
          type="text"
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <div className="template-grid">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="template-card">
            <div className="template-preview">
              <div className="preview-placeholder">
                📧
              </div>
            </div>
            <div className="template-info">
              <h4>{template.name}</h4>
              {template.description && <p>{template.description}</p>}
              <div className="template-meta">
                <small>{new Date(template.modifiedAt).toLocaleDateString()}</small>
              </div>
            </div>
            <div className="template-actions">
              <button
                className="btn-icon"
                onClick={() => onLoad(template.id)}
                title="Load Template"
              >
                <FolderOpen size={16} />
              </button>
              <button
                className="btn-icon"
                onClick={() => onDuplicate(template.id)}
                title="Duplicate"
              >
                <Copy size={16} />
              </button>
              <button
                className="btn-icon danger"
                onClick={() => onDelete(template.id, template.name)}
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
      {filteredTemplates.length === 0 && searchQuery && (
        <div className="empty-state">
          <p>No templates found</p>
          <small>Try a different search term</small>
        </div>
      )}
    </div>
  );
};

export default GrapesJsEditor;
